import { useCallback } from 'react';
import { useTrackUnsafe } from '../../segment/hooks';

/** Where the keyboard-shortcuts help dialog was opened from. */
export type Trigger = 'AvatarMenuProject' | 'AvatarMenuShares' | 'HelpShortcut';

/** Granularity of a shortcut reset. */
export type ResetScope = 'Single' | 'Category' | 'All';

/** PascalCase command-category enum values used by the Segment tracking plan. */
export type CommandCategoryEnum =
  | 'Comments'
  | 'Locate'
  | 'Manipulate'
  | 'Objects'
  | 'Panels'
  | 'Viewing';

/**
 * Maps the lowercase category name used internally (e.g. `viewing`) to the
 * PascalCase enum the tracking plan expects (e.g. `Viewing`). All current
 * category names are single lowercase words, so a simple capitalize is exact.
 */
export function toCommandCategoryEnum(category: string): CommandCategoryEnum {
  return (category.charAt(0).toUpperCase() +
    category.slice(1)) as CommandCategoryEnum;
}

type CustomizedParams = {
  commandId: string;
  /** New binding, or null when the binding was cleared to empty. */
  newKeys: string[] | null;
  /** Binding before this change: the customized value if any, else the default. */
  previousKeys: string[] | null;
  wasPreviouslyCustomized: boolean;
};

type ResetParams = {
  scope: ResetScope;
  /** Set when scope === 'Single'. */
  commandId?: string | null;
  /** PascalCase category; set when scope === 'Category'. */
  commandCategory?: CommandCategoryEnum | null;
};

/**
 * Tracking for the custom keyboard shortcuts feature.
 *
 * Mirrors the `useAIChatTracking` wrapper-of-`useTrack` convention. Account /
 * locale / OS context is attached automatically by `useTrack`; do not pass it.
 */
export function useKeyboardShortcutsTracking() {
  const track = useTrackUnsafe();

  const trackDialogOpened = useCallback(
    (trigger: Trigger) => {
      track('keyboard-shortcut-dialog-opened', { trigger });
    },
    [track]
  );

  const trackCustomized = useCallback(
    (params: CustomizedParams) => {
      track('keyboard-shortcut-customized', {
        command_id: params.commandId,
        new_keys: params.newKeys,
        previous_keys: params.previousKeys,
        was_previously_customized: params.wasPreviouslyCustomized,
      });
    },
    [track]
  );

  const trackReset = useCallback(
    (params: ResetParams) => {
      track('keyboard-shortcut-reset', {
        scope: params.scope,
        command_id: params.commandId ?? null,
        command_category: params.commandCategory ?? null,
      });
    },
    [track]
  );

  return { trackDialogOpened, trackCustomized, trackReset };
}
